import type { InterventionInOrderAlt, ScheduledIntervention } from 'api/models/mainservice';
import type { CustomForm } from 'common/models';
import type { MarkerIconType } from 'components/MapContainer/MarkerIcon';
import dayjs from 'dayjs';
import { compareDayjsDatesDesc } from 'helpers/comparing';
import type { Address, OrderInCustomer } from 'pages/customers/models';
import type { OrderAddRouteState } from 'pages/orders/components/addForm/OrderAddSummary';
import type {
  OpenOrderState,
  Order,
  OrderAddFormProggress,
  OrderAlt,
  OrderCreationSource,
  OrderDetail,
  OrderState
} from 'pages/orders/models';
import type { OrderInServiceObject } from 'pages/serviceObjects/models';
import type { NavigateFunction } from 'react-router-dom';

import { oneLineAddress } from './addressHelpers';
import { renderLegacyDateTime, renderTime } from './timeHelpers';

const LOCALSTORAGE_ORDER_ADD = 'orderAddForm';

export const orderStateName = (state: string) => {
  return `order.state.${state}.name`;
};

export const orderCreationSourceName = (source: OrderCreationSource) => {
  return `order.creationSource.${source}.name`;
};

export const orderStateTooltip = (state: string) => {
  return `order.state.${state}.tooltip`;
};

export const openOrderStateName = (state: OpenOrderState) => {
  return `order.openOrderState.${state}.name`;
};

export const openOrderStateTooltip = (state: OpenOrderState) => {
  return `order.openOrderState.${state}.tooltip`;
};

export const interventionStateName = (state: string) => {
  return `order.intervention.state.${state.toString()}.name`;
};

export const customFormNumberWithOrderCode = (
  order: Order | OrderInCustomer | OrderInServiceObject | OrderDetail,
  customForm: CustomForm
) => {
  return `${order.code} - ${customForm.codeNumber} - ${customForm.customFormTemplateName}`;
};

export const getInterventionMarkerIconType = (
  intervention: ScheduledIntervention
): MarkerIconType => {
  if (intervention.state === 'Planned') {
    return 'open';
  }

  if (intervention.state === 'DepartureStarted') {
    return 'departure';
  }

  if (intervention.state === 'Running') {
    return 'run';
  }

  if (intervention.state === 'WaitingToSignature') {
    return 'waiting';
  }

  if (intervention.state === 'WaitingToConfirm') {
    return 'waitingToConfirm';
  }

  if (intervention.state === 'Completed') {
    return 'completed';
  }

  if (intervention.state === 'Cancelled') {
    return 'cancel';
  }

  return 'info';
};

export const getOrderMarkerIconType = (order: OrderAlt) => {
  if (order.state === 'Open' && order.hasInterventions) {
    return 'hasIntervention';
  }

  if (order.state === 'Open') {
    return 'open';
  }

  return 'waiting';
};

export const getTransformedInterventionTime = (intervention: InterventionInOrderAlt) => {
  const startTimes = intervention.assignations.map((assignation) =>
    dayjs.utc(assignation.plannedStart)
  );

  const endTimes = intervention.assignations.map((assignation) =>
    dayjs.utc(assignation.plannedEnd)
  );

  const minTime = dayjs.min(startTimes);
  const maxTime = dayjs.max(endTimes);

  return `${renderLegacyDateTime(minTime)} - ${renderTime(maxTime)}`;
};

export const setOrderAddFormProgress = (data: OrderAddFormProggress) => {
  localStorage.setItem(LOCALSTORAGE_ORDER_ADD, JSON.stringify(data));
};

export const removeOrderAddFormProgress = () => localStorage.removeItem(LOCALSTORAGE_ORDER_ADD);

export const getOrderAddFormProgress = (): OrderAddFormProggress | undefined => {
  const data = localStorage.getItem(LOCALSTORAGE_ORDER_ADD);

  if (data) {
    removeOrderAddFormProgress();
    return JSON.parse(data);
  }

  return undefined;
};

export const handleNavigateToNewOrder = (
  navigate: NavigateFunction,
  state?: OrderAddRouteState
) => {
  removeOrderAddFormProgress();
  navigate(`/orders/new`, { state });
};

export const compareInterventionsDates = (a: InterventionInOrderAlt, b: InterventionInOrderAlt) => {
  const aStartTimes = a.assignations.map((assignation) => dayjs.utc(assignation.plannedStart));

  const bStartTimes = b.assignations.map((assignation) => dayjs.utc(assignation.plannedStart));

  const aTime = dayjs.min(aStartTimes);
  const bTime = dayjs.min(bStartTimes);

  return compareDayjsDatesDesc(aTime, bTime);
};

export const getOrderName = (order?: { code: string; address: Address }) => {
  if (order) return `${order.code} - ${oneLineAddress(order.address)}`;
};

export const getWorkHoursTranslateKey = (workHours: number) => {
  if (!Number.isInteger(workHours)) {
    return 'orders.aggregation.workHoursDecimal';
  }

  return 'orders.aggregation.workHoursCount';
};

interface OrderToFilter {
  state: OrderState;
  waitingForCustomerInfo: boolean;
  customerFilledInfo: boolean;
  hasInterventions: boolean;
}

export const filterOpenOrderState = (value: OpenOrderState | OrderState, record: OrderToFilter) => {
  if (value === 'CustomerFilledInfo') return record.customerFilledInfo;
  if (value === 'WaitingForCustomerInfo') return record.waitingForCustomerInfo;
  if (value === 'HasInterventions') return record.hasInterventions;
  if (value === 'ToPlan') return record.state === 'Open';

  return record.state === value;
};
